import React from "react";
import useSupabaseBrowser from "../hooks/use-supabase-browser";
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Tables } from "../../utils/database.types";
import { getValets } from "../api/get-valets";
import { Link } from "react-router-dom";
import ButtonAction from "./ButtonAction";

type RowData = Tables<"users"> & {
  venue: { id: string; name: string } | null;
};
export default () => {
  const supabase = useSupabaseBrowser();
  const {
    data: valets = [],
    isLoading,
    isError,
    refetch,
  } = useQuery(getValets(supabase));
  const columnHelper = createColumnHelper<RowData>();
  const columns = [
    columnHelper.accessor("full_name", {
      header: "Full Name",
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("email", {
      header: "Email",
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),

    columnHelper.accessor("roles", {
      header: "Roles",
      cell: (info) => <div>{(info.getValue() || []).join(", ")}</div>,
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("venue", {
      header: "Venue",
      cell: (info) => {
        const venue = info.getValue();

        if (!venue) {
          return "-";
        }
        return <Link to={`/venues/${venue.id}/edit`}>{venue.name}</Link>;
      },
      footer: (info) => info.column.id,
    }),
    columnHelper.display({
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="join">
            <Link
              className="btn btn-xs join-item btn-warning"
              to={`/valets/${row.original.id}/edit`}
            >
              Edit
            </Link>

            <ButtonAction
              subject="users"
              text={row.original.status === "active" ? "Suspend" : "Activate"}
              mutationData={{
                status:
                  row.original.status === "active" ? "suspended" : "active",
                id: row.original.id,
              }}
              onSuccess={refetch}
            />

            <ButtonAction
              subject="users"
              text={"Archive"}
              mutationData={{
                archived: true,
                id: row.original.id,
              }}
              onSuccess={refetch}
              confirmationText={`Are you sure you want to archive ${row.original.full_name}? This action cannot be undone.`}
            />

            <ButtonAction
              subject="users"
              text={
                row.original.roles?.includes("admin")
                  ? "Remove admin rights"
                  : "Make admin"
              }
              mutationData={{
                roles: row.original.roles?.includes("admin")
                  ? ["valet"]
                  : ["valet", "admin"],
                id: row.original.id,
              }}
              onSuccess={refetch}
              confirmationText={
                row.original.roles?.includes("admin")
                  ? `Are you sure you want to remove admin rights from ${row.original.full_name}?`
                  : `Are you sure you want to make ${row.original.full_name} an admin?`
              }
            />
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: valets ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return <span className="loading loading-dots loading-lg"></span>;
  }

  if (isError || !valets) {
    return <div>Error</div>;
  }

  return (
    <section className="card overflow-x-auto bg-base-100 shadow-sm">
      <table className="table table-zebra">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <div>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};
